import { Box, Button } from "@mui/material";
import { toReferringAgencyRefs } from "@/utils/referring-agency-utils";
import { useQueryClient } from "@tanstack/react-query";
import { FormikProps, useFormik } from "formik";
import { cloneDeep, isEqual, omit } from "lodash";
import { memo, useMemo, useState } from "react";
import Flex from "../../../../components/Flex";
import LoadingOverlay from "../../../../components/LoadingOverlay";
// import { ScrollArea } from "../../../../components/ScrollBar";
import Text from "../../../../components/Text";
import ConfirmationModal from "../../../../components/app/ConfirmationModal";
import ContactInformation from "../../../../components/app/provider/patient/add-new-patient/ContactInformation";
import EmergencyContact from "../../../../components/app/provider/patient/add-new-patient/EmergencyContact";
import Insurance from "../../../../components/app/provider/patient/add-new-patient/Insurance";
import PatientDetail from "../../../../components/app/provider/patient/add-new-patient/PatientDetail";
import PrivacyAndOtherConsent from "../../../../components/app/provider/patient/add-new-patient/PrivacyConsent";
import ProviderInformation from "../../../../components/app/provider/patient/add-new-patient/ProviderInformation";
import Preferences from "../../../../components/app/provider/patient/add-new-patient/preferences/Preferences";
import {
  ADD_NEW_PATIENT_INITIAL_VALUES,
  ADD_NEW_PATIENT_VALIDATION_SCHEMA,
  NewPatientForm,
} from "../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useFormScrollIntoView } from "../../../../hooks/use-form-scrollInto-view";
// import { useQueryParams } from "../../../../hooks/use-query-param";
import { useNavigate } from "react-router-dom";
import ModalContentContainer from "../../../../components/globalModal/ModalContentContainer";
import { useQueryParams } from "../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useEditPatient } from "../../../../queries/use-edit-patient";
import { useLanguageList } from "../../../../queries/use-language-list";
import { useChangePatientAvatar } from "../../../../queries/use-patient-change-avatar";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
} from "../../../../utils/utils";
import { getStoredRealm } from "@/utils/auth-storage";
interface UpdatePatientFormProps {
  formHeight?: string;
  prefillValues: NewPatientForm;
  closeDrawer?: (id?: any) => void;
  onCancel?: () => void;
  disableScrollArea?: boolean;
}

// note: this is edit patient form without scroll refs
const UpdatePatientForm = ({
  prefillValues,
  closeDrawer,
  onCancel,
  // formHeight = "calc(100vh - 240px)",
}: UpdatePatientFormProps) => {
  const { mutateAsync: editPatient } = useEditPatient();
  const fromCheckIn = useQueryParams("fromCheckIn");

  const navigateBackDisclosure = useDisclosure(false);
  const showSnackbar = useSnackbar();
  const xTENANTID = getStoredRealm();

  const { data: languageList } = useLanguageList({
    xTENANTID: xTENANTID ?? undefined,
  });

  const enLanguage = useMemo(() => {
    const lang = languageList?.data?.content?.find(
      (it) => it.name === "English"
    );
    return lang ? [lang] : undefined;
  }, [languageList]);

  const { mutateAsync: ChangePatientAvatar } = useChangePatientAvatar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const formik = useFormik({
    initialValues: prefillValues ?? {
      ...ADD_NEW_PATIENT_INITIAL_VALUES,
      languages: enLanguage,
    },
    validationSchema: ADD_NEW_PATIENT_VALIDATION_SCHEMA,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit,
    enableReinitialize: true,
  });
  // const isDemoGraphicsDrawer =
  //   location?.pathname?.split("/")?.[
  //     location?.pathname?.split("/")?.length - 1
  //   ] === "demographics";

  useFormScrollIntoView(formik);

  function handleResetForm(res: any) {
    showSnackbar(getSuccessSnackbar(res));
    // setTimeout(() => {
    // if (isDemoGraphicsDrawer) {
    closeDrawer?.((formik.values as any)?.uuid);
    // } else {
    //   navigate(
    //     `/app/provider/scheduling/appointment?openCheckIn=true&patientId=${
    //       (formik.values as any)?.uuid
    //     }`
    //   );
    // }
    // }, 2000);
    formik.resetForm();
  }

  function onSubmit(values: NewPatientForm) {
    const date = values.birthDate ? new Date(values.birthDate) : undefined;
    if (date) date.setHours(0, 0, 0, 0);

    setIsLoading(true);
    // Trim leading/trailing spaces for first and last name
    const copiedValues: any = cloneDeep(values);

    copiedValues.firstName = copiedValues?.firstName.trim();
    copiedValues.lastName = copiedValues?.lastName.trim();
    copiedValues.middleName = copiedValues?.middleName?.trim();

    copiedValues.patientInsurances = values.patientInsurances?.map((it) => ({
      ...it,
      provider: it?.provider
        ? {
            [it?.provider.value]: `${it.provider.label}`,
          }
        : undefined,
      insurancePayer: {
        ...it.insurancePayer,
        payerId: it?.insurancePayer?.payerId?.value,
      },
      planId: it?.planId?.value,
      planName: it?.planId?.label,
      birthDate: date?.toISOString(),
    }));

    copiedValues.assessments = {
      assessment: values.assessments?.assessment?.map((v) => v.value),
      scheduleTime: values.assessments?.recordedDate,
      reminderMedium: values.assessments?.reminderMedium
        ? [values.assessments?.reminderMedium]
        : undefined,
    };

    copiedValues.referringAgencies = toReferringAgencyRefs(
      values.referringAgencies
    );

    copiedValues.pharmacyLabRadiology = values.pharmacyLabRadiologyMap?.reduce(
      (acc, currVal) => {
        acc[currVal.uuid] = currVal.name;
        return acc;
      },
      {} as Record<string, string>
    );

    const transFormData: any = getUnNecessaryTransformData(["languages"], {
      ...copiedValues,
      birthDate: date?.toISOString(),
    });

    const payload = {
      patient: transFormData,
      xTENANTID: xTENANTID ?? undefined,
    };

    if (prefillValues) {
      editPatient(payload)
        .then((res) => {
          queryClient.invalidateQueries({
            queryKey: ["provider-group-patients-list"],
          });
          queryClient.invalidateQueries({
            queryKey: ["patient"],
          });
          if (!values?.avatar?.includes(".amazonaws.com")) {
            return ChangePatientAvatar({
              xTENANTID: getStoredRealm() ?? "",
              avatar: {
                newAvatar: values.avatar,
                userId: (copiedValues as any).uuid ?? "",
              },
            }).then((avatarResp) => {
              handleResetForm(res);
              if (isEqual(omit(values, "avatar"), omit(copiedValues, "avatar")))
                return avatarResp;

              return res;
            });
          }
          showSnackbar(getSuccessSnackbar(res));
          handleResetForm(res);

          return res;
        })
        .catch((err) => showSnackbar(getErrorSnackbar(err)))
        .finally(() => setIsLoading(false));
    }
  }

  const navigate = useNavigate();

  return (
    <ModalContentContainer
      footer={
        <Flex width={"100%"} justifyContent={"end"} gap={2} mt={1}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={formik.submitForm} variant="contained">
            Save
          </Button>
        </Flex>
      }
    >
      <Box>
        <LoadingOverlay isLoading={isLoading} />

        <ConfirmationModal
          maxWidth={"md"}
          disclosureHost={navigateBackDisclosure}
          cancelText="No"
          onConfirm={() => {
            fromCheckIn === "true"
              ? navigate(
                  `/app/provider/scheduling/appointment?openCheckIn=true&patientId=${
                    (formik.values as any)?.uuid
                  }`
                )
              : navigate(-1);
          }}
          confirmationText="Yes"
          title={
            <Text fontWeight={"medium"} fontSize={"24px"}>
              Do You want to leave without saving your changes?
            </Text>
          }
        />
        <Form
          formik={formik}
          languageList={languageList?.data?.content ?? []}
        />
      </Box>
    </ModalContentContainer>
  );
};

export default UpdatePatientForm;

const Form = memo(
  ({
    formik,
    languageList,
  }: {
    formik: FormikProps<NewPatientForm>;
    languageList: any;
  }) => (
    <Box gap={"10px"} mt={1}>
      <Flex flexDirection={"column"} gap={"20px"}>
        <ProviderInformation formik={formik} />
        <PatientDetail formik={formik} languageList={languageList} />
        <ContactInformation formik={formik} />
        <EmergencyContact formik={formik} />
        <Insurance formik={formik} showCheckEligibility={false} />
        <Preferences formik={formik} />
        <PrivacyAndOtherConsent formik={formik} />
      </Flex>
    </Box>
  )
);
